import { useTranslation } from "react-i18next";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Box, TextField, FormLabel, FormControl, Autocomplete, Divider, Button } from "@mui/material";

import { useCountryListing } from "../../../../../../../api/queries/network/useCountryListing";

import { AddEntityLocationsFormData } from "../../../../../types/editEntityProfile-type";
import BranchDetails from "./BranchDetails";

interface CountryOption {
  label: string;
  value: number;
}

const EntityBranchLocation = ({ index }: { index: number }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });
  const { countries, isCountriesDataLoading } = useCountryListing();
  const { control, setValue } = useFormContext<AddEntityLocationsFormData>();

  const branches = useWatch({ control, name: `locations.${index}.branches` }) || [];

  const handleAddingAnotherBranch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setValue(`locations.${index}.branches`, [
      ...branches,
      {
        branchName: "",
        branchAddress: "",
        branchGoogleMapsAddress: "",
        isHeadquarters: false,
        workingHours: {},
        sports: [],
        facilities: [] as { facilityId: number; count: number }[],
      },
    ]);
  };

  return (
    <Box
      sx={{
        padding: "16px",
        border: "1px solid #EAECF0",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Controller
        name={`locations.${index}.country`}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth>
            <FormLabel
              sx={{
                color: "var(--Dark---800, #414141)",
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "24px",
              }}
            >
              {t("country")}
            </FormLabel>
            <Autocomplete
              value={countries?.find((country: CountryOption) => country.value === field.value) || null}
              onChange={(_, newValue: CountryOption | null) => {
                field.onChange(newValue?.value || 0);
                if (newValue?.value && branches.length === 0) {
                  setValue(`locations.${index}.branches`, [
                    {
                      branchName: "",
                      branchAddress: "",
                      branchGoogleMapsAddress: "",
                      isHeadquarters: false,
                      workingHours: {},
                      sports: [],
                      facilities: [],
                    },
                  ]);
                }
              }}
              loading={isCountriesDataLoading}
              options={countries || []}
              getOptionLabel={(option: CountryOption) => option.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("selectCountry")}
                  error={!!error}
                  helperText={error && t(error.message)}
                />
              )}
            />
          </FormControl>
        )}
      />
      {branches.length > 0 &&
        branches.map((_, branchIndex) => (
          <BranchDetails key={branchIndex} locationIdx={index} branchIdx={branchIndex} />
        ))}
      {branches.length > 0 && <AddAnotherBranchButton handleAddingAnotherBranch={handleAddingAnotherBranch} />}
    </Box>
  );
};

export default EntityBranchLocation;

const AddAnotherBranchButton = ({
  handleAddingAnotherBranch,
}: {
  handleAddingAnotherBranch: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "entityProfileEdit" });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
      }}
    >
      <Divider orientation="horizontal" sx={{ flex: 1 }} />
      <Button
        type="button"
        sx={{
          color: "var(--Gray-700, #344054) !important",
          fontFamily: "Montserrat",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "20px",
          padding: "10px 24px",
          borderRadius: "80px",
          border: "1px solid var(--Gray-300, #D0D5DD)",
          background: "var(--Base-White, #FFF) !important",
          boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
        }}
        onClick={handleAddingAnotherBranch}
      >
        {t("addAnotherBranch")}
      </Button>
      <Divider orientation="horizontal" sx={{ flex: 1 }} />
    </Box>
  );
};
